import { IdentificationCardIcon } from "@phosphor-icons/react/dist/csr/IdentificationCard";
import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { Alert } from "@volca/ui/components/ui/alert";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import {
  PageContainer,
  PageMainCard,
} from "@volca/ui/components/ui/page-components";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { useBusiness } from "@/core/business";
import { useGetMyUser } from "@/user/query";
import { getPayrollRosterApiErrorMessage } from "./api-errors";
import { PayrollRosterEditor } from "./payroll-roster-editor";
import { PayrollRosterLoadingState } from "./payroll-roster-loading-state";
import {
  canEditPayrollRoster,
  canSyncPayrollRosterTechnicians,
  useCanViewPayrollRoster,
} from "./permissions";
import { usePayrollRoster } from "./query";
import { RosterPageHeader } from "./roster-page-header";
import { RosterReadOnlyView } from "./roster-read-only-view";

const DESCRIPTION =
  "Manage the people and attributes used to calculate payroll.";

const PayrollRosterPage = () => {
  const {
    businessId,
    selectedBusiness,
    isLoading: businessLoading,
  } = useBusiness();
  const { data: user, isLoading: userLoading } = useGetMyUser();
  const {
    accessQuery,
    canViewRoster,
    isLoading: rosterAccessLoading,
  } = useCanViewPayrollRoster(selectedBusiness, user);
  const rosterQuery = usePayrollRoster(canViewRoster ? businessId : null);

  if (businessLoading || userLoading || rosterAccessLoading) {
    return <PayrollRosterLoadingState />;
  }

  if (!canViewRoster) {
    return (
      <PageContainer variant="workspace">
        <RosterPageHeader description={DESCRIPTION} />
        <Empty
          className={emptyStateVariants({ layout: "panel" })}
          role={accessQuery.isError ? "alert" : undefined}
        >
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {accessQuery.isError ? <WarningCircleIcon /> : <LockIcon />}
            </EmptyMedia>
            <EmptyTitle>
              {accessQuery.isError
                ? "Payroll access could not be checked"
                : "You don't have access to this roster"}
            </EmptyTitle>
            <EmptyDescription>
              {accessQuery.isError
                ? "Refresh the page to try again."
                : "Ask a manager of this business for roster access."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </PageContainer>
    );
  }

  if (!rosterQuery.isFetchedAfterMount && !rosterQuery.isError) {
    return <PayrollRosterLoadingState />;
  }

  const isRosterReady = Boolean(
    businessId && rosterQuery.isSuccess && rosterQuery.isFetchedAfterMount,
  );
  const canEdit = canEditPayrollRoster(
    selectedBusiness,
    user,
    accessQuery.data,
    rosterQuery.data,
  );

  if (isRosterReady && businessId && rosterQuery.data && canEdit) {
    return (
      <PayrollRosterEditor
        key={`${businessId}-${rosterQuery.data.baseline?.version_number ?? rosterQuery.data.version_number ?? "current"}`}
        roster={rosterQuery.data}
        businessId={businessId}
        canSyncTechnicians={canSyncPayrollRosterTechnicians(
          selectedBusiness,
          user,
        )}
      />
    );
  }

  return (
    <PageContainer variant="workspace">
      <RosterPageHeader description={DESCRIPTION} />

      {rosterQuery.isError && (
        <Alert color="danger">
          Payroll roster could not be loaded.{" "}
          {getPayrollRosterApiErrorMessage(rosterQuery.error)}
        </Alert>
      )}

      {rosterQuery.isSuccess && !rosterQuery.data && (
        <Empty className={emptyStateVariants({ layout: "panel" })}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IdentificationCardIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>No payroll roster configured</EmptyTitle>
            <EmptyDescription>
              Ask your Volca contact to configure the roster fields for this
              business before adding people.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {isRosterReady && businessId && rosterQuery.data && (
        <PageMainCard>
          <RosterReadOnlyView
            key={businessId}
            businessId={businessId}
            roster={rosterQuery.data}
          />
        </PageMainCard>
      )}
    </PageContainer>
  );
};

export default PayrollRosterPage;
